import { useState, useEffect, useCallback, useMemo } from 'react';
import { bookmarkService } from '#/services/userData';
import Bookmark from '#/models/Bookmark/bookmark';

/**
 * @param dataRef {Object} React ref to avoid re-definitions of callbacks
 * @param dataRef.progress {number} The progress of the playback.
 * @param dataRef.duration {number} The duration of the asset.
 * @param dataRef.asset {object} The asset object.
 * @returns {Object}
 * @returns {Array} userBookmarks
 * @returns {Function} getUserBookmarks
 * @returns {Function} setBookmark
 * @returns {Function} removeBookmark
 * @returns {Function} getBookmarkById
 * @returns {Function} clearBookmark
 * @returns {Function} isBookmark
 */

const useBookmarks = ({ dataRef, isAuthenticated, userName }) => {
  const {
    getContinueWatchingItems,
    getBookmark,
    addBookmark,
    removeBookmark,
    clearBookmark,
    isBookmark
  } = useMemo(() => bookmarkService(userName), [userName]);

  const [userBookmarks, setUserBookmarks] = useState();

  const getUserBookmarks = useCallback(async () => {
    let bookmarks = [];
    if (isAuthenticated) {
      bookmarks = await getContinueWatchingItems();
    }

    return bookmarks;
  }, [isAuthenticated, getContinueWatchingItems]);

  const setBookmark = useCallback(async () => {
    if (!isAuthenticated || !dataRef) {
      return [];
    }
    const { progress, duration, asset } = dataRef.current;

    const progressPercentage = (progress * 100) / duration;

    const bookmark = Bookmark({
      ...asset,
      progress: progressPercentage,
      resumeTime: progress
    });
    addBookmark(bookmark);
  }, [dataRef, isAuthenticated, addBookmark]);

  const getBookmarkById = useCallback(
    ({ id: assetId }) => {
      if (!isAuthenticated) {
        return null;
      }
      return getBookmark(assetId);
    },
    [isAuthenticated, getBookmark]
  );

  useEffect(() => {
    const loadBookmarks = async () => {
      setUserBookmarks(await getUserBookmarks());
    };
    if (!userBookmarks) {
      loadBookmarks();
    }
  }, [userBookmarks, getUserBookmarks]);

  return {
    userBookmarks,
    getUserBookmarks,
    setBookmark,
    removeBookmark,
    getBookmarkById,
    clearBookmark,
    isBookmark
  };
};

export default useBookmarks;
